import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlaneIcon, 
  SuitcaseIcon, 
  MapPinIcon, 
  ShieldIcon, 
  StarIcon, 
  GlobeIcon, 
  CheckIcon, 
  TrophyIcon,
  ClockIcon,
  PhoneIcon,
  EmailIcon,
  WhatsAppIcon
} from '../components/Icons';
import api from '../services/api';
import SEO from '../components/SEO';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const data = await api.getServices();
      setServices(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to load services. Please try again later.');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // Icon mapping for dynamic icon selection
  const iconMap = {
    PlaneIcon,
    SuitcaseIcon,
    MapPinIcon,
    ShieldIcon,
    StarIcon,
    GlobeIcon,
    TrophyIcon,
  };

  const getIcon = (iconName) => {
    return iconMap[iconName] || SuitcaseIcon;
  };

  return (
    <div className="flex-1">
      <SEO
        title="Our Services"
        description="Travel packages, flight and hotel bookings, guided tours, spiritual journeys, and customized itineraries. Comprehensive travel solutions from Happy Yatra Travels."
        keywords="travel services, tour booking, flight booking, hotel booking, guided tours, customized travel, spiritual tours"
        canonical="/services"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-blue via-dark-blue to-primary-blue overflow-hidden py-20 md:py-32">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7z' fill='%23ffffff' fill-opacity='0.1'/%3E%3C/svg%3E")`,
              backgroundSize: '50px 50px'
            }}
          ></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full border border-white/30">
            <PlaneIcon className="w-5 h-5 text-yellow-300" />
            <span className="text-white font-semibold text-sm">Comprehensive Travel Solutions</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight text-white">
            Our Services
          </h1>
          
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            From customized travel packages to spiritual journeys, we offer comprehensive travel services to make your journey seamless and memorable
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
              <p className="mt-4 text-gray-600">Loading services...</p>
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {!loading && !error && services.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">No services available at the moment.</p>
            </div>
          )}

          {!loading && !error && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => {
                const IconComponent = getIcon(service.icon_name || 'SuitcaseIcon');
              return (
                <div
                  key={service.id}
                  className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100 group"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-blue to-primary-orange flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-primary-blue transition-colors">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
                      Key Features:
                    </h4>
                    <ul className="space-y-2">
                      {(service.features || []).map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-gray-700">
                          <CheckIcon className="w-5 h-5 text-primary-orange flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
              Why Choose Our Services?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We go beyond just booking your trip - we ensure every aspect of your journey is perfect
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ShieldIcon, title: 'Trusted & Reliable', desc: '5+ years of experience in travel industry' },
              { icon: ClockIcon, title: '24/7 Support', desc: 'Round-the-clock assistance during your journey' },
              { icon: StarIcon, title: 'Best Prices', desc: 'Competitive pricing with no hidden charges' },
              { icon: CheckIcon, title: 'Expert Team', desc: 'Experienced travel consultants to guide you' }
            ].map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 text-center"
                >
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-blue to-primary-orange flex items-center justify-center">
                      <FeatureIcon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-primary-blue to-dark-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Plan Your Journey?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get in touch with our travel experts and let us help you create the perfect travel experience
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/917718838808"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-primary-orange text-white rounded-xl font-semibold hover:bg-orange-600 transform hover:scale-105 transition-all duration-300 shadow-lg inline-flex items-center justify-center gap-2"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Chat on WhatsApp
            </a>
            <a
              href="tel:+917718838808"
              className="px-8 py-4 bg-white/20 backdrop-blur-md text-white rounded-xl font-semibold hover:bg-white/30 border-2 border-white/50 transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              <PhoneIcon className="w-5 h-5" />
              Call Us Now
            </a>
            <Link
              to="/packages"
              className="px-8 py-4 bg-white text-primary-blue rounded-xl font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-lg"
            >
              View Packages
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
